import React, { useState, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  User, Mail, Phone, Building, Briefcase, Users, MapPin, Globe,
  Shield, Activity, Bell, CreditCard, Download, AlertTriangle,
  Settings, BarChart3, Calendar, Edit, Save, X, Upload, Check,
  Lock, Smartphone, Zap, Camera, Sun, Moon, LogOut
} from 'lucide-react';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useProfilePhoto } from '../hooks/useProfilePhoto';
import { useSubscriptionStatusContext } from '../hooks/SubscriptionStatusContext';
import { Avatar } from '../components/Avatar';
import { showErrorToast, showSuccessToast } from '../utils/toastHelpers';
import { leadsApi, dataApi } from '../services/api';
import { useLeads, useVendas } from '../hooks/useApi';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
  padding-bottom: ${props => props.theme.spacing[8]};
`;

const HeroSection = styled.div<{ $bannerUrl?: string }>`
  position: relative;
  height: 280px;
  background: ${props => props.$bannerUrl
    ? `url(${props.$bannerUrl}) center/cover`
    : `linear-gradient(135deg, ${props.theme.colors.primary}dd 0%, ${props.theme.colors.primaryDark} 100%)`
  };
  border-radius: ${props => props.theme.borders.radius.xl};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing[6]};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    &::after {
      opacity: 1;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$bannerUrl
      ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)'
      : 'url("data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 1200 120\"><path d=\"M0,0 Q300,120 600,60 T1200,0\" fill=\"rgba(255,255,255,0.1)\"/></svg>") repeat-x bottom'
    };
    opacity: ${props => props.$bannerUrl ? 1 : 0.3};
  }

  &::after {
    content: '';
    position: absolute;
    top: ${props => props.theme.spacing[4]};
    right: ${props => props.theme.spacing[4]};
    width: 48px;
    height: 48px;
    border-radius: ${props => props.theme.borders.radius.full};
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const BannerUploadIcon = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing[4]};
  right: ${props => props.theme.spacing[4]};
  width: 48px;
  height: 48px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;

  ${HeroSection}:hover & {
    opacity: 1;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  bottom: -60px;
  left: ${props => props.theme.spacing[8]};
  display: flex;
  align-items: flex-end;
  gap: ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    left: ${props => props.theme.spacing[4]};
    flex-direction: column;
    align-items: center;
    bottom: -100px;
  }
`;

const ProfilePhotoWrapper = styled.div`
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const HeroInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  padding-bottom: ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    align-items: center;
    text-align: center;
  }
`;

const UserName = styled.h1`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const UserRole = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textSecondary};
`;

const Badge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  border: 1px solid ${props => props.$color}40;
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${props => props.theme.spacing[6]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const SidebarItem = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.$active ? props.theme.colors.primary + '15' : 'transparent'};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary + '40' : 'transparent'};
  border-radius: ${props => props.theme.borders.radius.lg};
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary + '20' : props.theme.colors.backgroundTertiary};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.border};
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const Section = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const SectionIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const SectionName = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
  ${props => props.$fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return props.theme.colors.primary;
      case 'danger': return props.theme.colors.error;
      default: return props.theme.colors.backgroundTertiary;
    }
  }};
  color: ${props => props.$variant === 'secondary' ? props.theme.colors.textPrimary : 'white'};
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'primary': return props.theme.colors.primary;
      case 'danger': return props.theme.colors.error;
      default: return props.theme.colors.border;
    }
  }};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing[4]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const InfoCard = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[4]};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const HiddenInput = styled.input`
  display: none;
`;

const DangerZone = styled.div`
  background: ${props => props.theme.colors.error}10;
  border: 2px solid ${props => props.theme.colors.error}40;
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
`;

const DangerTitle = styled.h3`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  margin: 0 0 ${props => props.theme.spacing[4]} 0;
`;

type TabType = 'personal' | 'professional' | 'security' | 'stats' | 'preferences' | 'subscription' | 'danger';

const ToggleSwitch = styled.label<{ $checked: boolean }>`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.backgroundTertiary};
    border: 1px solid ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.border};
    border-radius: 24px;
    transition: all 0.3s ease;

    &:before {
      content: "";
      position: absolute;
      height: 18px;
      width: 18px;
      left: ${props => props.$checked ? '26px' : '3px'};
      bottom: 2px;
      background: white;
      border-radius: 50%;
      transition: all 0.3s ease;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  }
`;

const PhotoModal = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  cursor: pointer;
`;

const PhotoModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhotoModalImage = styled.img`
  width: 400px;
  height: 400px;
  border-radius: 50%;
  box-shadow: ${props => props.theme.shadows.xl};
  object-fit: cover;
  border: 4px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    width: 300px;
    height: 300px;
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 250px;
    height: 250px;
  }
`;

const PhotoModalClose = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.error};
    transform: scale(1.1);
  }
`;

const Profile: React.FC = () => {
  const theme = useTheme();
  const { user } = useCurrentUser();
  const { status: subscriptionStatus } = useSubscriptionStatusContext();
  const { profilePhoto, setProfilePhoto, removeProfilePhoto } = useProfilePhoto();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // Buscar dados de leads e vendas
  const { data: leadsData } = useLeads();
  const { data: vendasData } = useVendas();

  const leads = leadsData?.data?.leads || [];
  const vendas = vendasData?.data?.vendas || [];
  const vendasPagas = vendas.filter((v) => v.status?.toLowerCase() === 'pago');
  const receitaTotal = vendasPagas.reduce((sum, v) => {
    let valor = v.valor;
    if (typeof valor === 'string') {
      valor = valor.replace('R$', '').replace('.', '').replace(',', '.');
    }
    return sum + (parseFloat(valor) || 0);
  }, 0);

  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [bannerPhoto, setBannerPhoto] = useState<string | null>(null);

  // Estados para preferências
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('loopra-theme') === 'dark';
  });
  const [emailNotifications, setEmailNotifications] = useState(() => {
    return localStorage.getItem('loopra-email-notifications') !== 'false';
  });
  const [panelNotifications, setPanelNotifications] = useState(() => {
    return localStorage.getItem('loopra-panel-notifications') !== 'false';
  });

  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    phone: '',
    bio: '',
    location: '',
    website: '',
    company: '',
    position: '',
    department: '',
  });

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleBannerClick = () => {
    bannerInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showErrorToast('Por favor, selecione apenas arquivos de imagem.', theme);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('A imagem deve ter no máximo 5MB.', theme);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setProfilePhoto(result);
        showSuccessToast('Foto atualizada com sucesso!', theme);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      showErrorToast('Erro ao processar a imagem.', theme);
    }
  };

  const handleBannerChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showErrorToast('Por favor, selecione apenas arquivos de imagem.', theme);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showErrorToast('A imagem deve ter no máximo 5MB.', theme);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setBannerPhoto(result);
        if (user?.uid) {
          localStorage.setItem(`loopra_banner_${user.uid}`, result);
        }
        showSuccessToast('Banner atualizado com sucesso!', theme);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      showErrorToast('Erro ao processar a imagem.', theme);
    }
  };

  const handleSave = () => {
    // Salvar no localStorage ou backend
    if (user?.uid) {
      localStorage.setItem(`loopra_profile_${user.uid}`, JSON.stringify(profileData));
      showSuccessToast('Perfil atualizado com sucesso!', theme);
      setIsEditing(false);
    }
  };

  const handleToggleTheme = () => {
    const newTheme = darkMode ? 'light' : 'dark';
    localStorage.setItem('loopra-theme', newTheme);
    setDarkMode(!darkMode);
    showSuccessToast(`Tema ${newTheme === 'dark' ? 'escuro' : 'claro'} ativado! Recarregue a página para aplicar.`, theme);
  };

  const handleToggleEmailNotifications = () => {
    const newValue = !emailNotifications;
    localStorage.setItem('loopra-email-notifications', newValue.toString());
    setEmailNotifications(newValue);
    showSuccessToast(`Notificações por email ${newValue ? 'ativadas' : 'desativadas'}!`, theme);
  };

  const handleTogglePanelNotifications = () => {
    const newValue = !panelNotifications;
    localStorage.setItem('loopra-panel-notifications', newValue.toString());
    setPanelNotifications(newValue);
    showSuccessToast(`Notificações no painel ${newValue ? 'ativadas' : 'desativadas'}!`, theme);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      window.location.href = '/';
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      showErrorToast('Erro ao sair da conta. Tente novamente.', theme);
    }
  };

  // Carregar dados salvos
  React.useEffect(() => {
    if (user?.uid) {
      const saved = localStorage.getItem(`loopra_profile_${user.uid}`);
      if (saved) {
        setProfileData(JSON.parse(saved));
      }

      const savedBanner = localStorage.getItem(`loopra_banner_${user.uid}`);
      if (savedBanner) {
        setBannerPhoto(savedBanner);
      }
    }
  }, [user?.uid]);

  const renderContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.primary}>
                  <User size={20} />
                </SectionIcon>
                <SectionName>Informações Pessoais</SectionName>
              </SectionTitle>
              {!isEditing ? (
                <Button $variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit size={16} />
                  Editar
                </Button>
              ) : (
                <div style={{ display: 'flex', gap: theme.spacing[3] }}>
                  <Button $variant="primary" onClick={handleSave}>
                    <Save size={16} />
                    Salvar
                  </Button>
                  <Button $variant="secondary" onClick={() => setIsEditing(false)}>
                    <X size={16} />
                    Cancelar
                  </Button>
                </div>
              )}
            </SectionHeader>

            <div style={{ marginBottom: theme.spacing[6], textAlign: 'center' }}>
              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              <div
                onClick={() => {
                  if (profilePhoto || user?.photoURL) {
                    setShowPhotoModal(true);
                  }
                }}
                style={{
                  display: 'inline-block',
                  cursor: (profilePhoto || user?.photoURL) ? 'pointer' : 'default',
                  position: 'relative'
                }}
                title={(profilePhoto || user?.photoURL) ? 'Clique para ampliar' : ''}
              >
                <Avatar
                  name={profileData.displayName || user?.displayName}
                  email={user?.email}
                  size="xl"
                  src={profilePhoto || user?.photoURL || undefined}
                  alt="Foto de perfil"
                />
              </div>
              <div style={{ marginTop: theme.spacing[3], display: 'flex', gap: theme.spacing[2], justifyContent: 'center' }}>
                <Button $variant="secondary" onClick={handlePhotoClick}>
                  <Upload size={16} />
                  Alterar Foto
                </Button>
                {profilePhoto && (
                  <Button $variant="secondary" onClick={() => {
                    removeProfilePhoto();
                    showSuccessToast('Foto removida com sucesso!', theme);
                  }}>
                    <X size={16} />
                    Remover
                  </Button>
                )}
              </div>
            </div>

            <FormGrid>
              <FormGroup>
                <Label>Nome Completo</Label>
                <Input
                  value={profileData.displayName}
                  onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                  placeholder="Seu nome completo"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="seu@email.com"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup>
                <Label>Telefone</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="+55 51 99999999"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup>
                <Label>Localização</Label>
                <Input
                  value={profileData.location}
                  onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                  placeholder="Rio Grande do Sul"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup $fullWidth>
                <Label>Website</Label>
                <Input
                  value={profileData.website}
                  onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                  placeholder="https://seu-site.com"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup $fullWidth>
                <Label>Bio</Label>
                <TextArea
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Conte um pouco sobre você..."
                  disabled={!isEditing}
                />
              </FormGroup>
            </FormGrid>
          </Section>
        );

      case 'professional':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.info}>
                  <Briefcase size={20} />
                </SectionIcon>
                <SectionName>Informações Profissionais</SectionName>
              </SectionTitle>
              {!isEditing ? (
                <Button $variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit size={16} />
                  Editar
                </Button>
              ) : (
                <div style={{ display: 'flex', gap: theme.spacing[3] }}>
                  <Button $variant="primary" onClick={handleSave}>
                    <Save size={16} />
                    Salvar
                  </Button>
                  <Button $variant="secondary" onClick={() => setIsEditing(false)}>
                    <X size={16} />
                    Cancelar
                  </Button>
                </div>
              )}
            </SectionHeader>

            <FormGrid>
              <FormGroup>
                <Label>Empresa</Label>
                <Input
                  value={profileData.company}
                  onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                  placeholder="Nome da empresa"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup>
                <Label>Cargo</Label>
                <Input
                  value={profileData.position}
                  onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                  placeholder="Seu cargo"
                  disabled={!isEditing}
                />
              </FormGroup>

              <FormGroup $fullWidth>
                <Label>Departamento</Label>
                <Input
                  value={profileData.department}
                  onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                  placeholder="Marketing, Vendas, TI..."
                  disabled={!isEditing}
                />
              </FormGroup>
            </FormGrid>
          </Section>
        );

      case 'security':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.warning}>
                  <Shield size={20} />
                </SectionIcon>
                <SectionName>Segurança</SectionName>
              </SectionTitle>
            </SectionHeader>

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <InfoCard>
                <Check size={20} style={{ color: theme.colors.success }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Autenticação Google</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Conectado como {user?.email}
                  </div>
                </div>
                <Badge $color={theme.colors.success}>Ativo</Badge>
              </InfoCard>

              <InfoCard>
                <Smartphone size={20} style={{ color: theme.colors.info }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Dispositivo Atual</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    {(() => {
                      const ua = navigator.userAgent;
                      if (ua.includes('Mac')) return 'MacBook';
                      if (ua.includes('Windows')) return 'Windows';
                      if (ua.includes('Linux')) return 'Linux';
                      if (ua.includes('Android')) return 'Android';
                      if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
                      return 'Navegador Web';
                    })()} • Última atividade: agora
                  </div>
                </div>
              </InfoCard>

              <InfoCard>
                <Lock size={20} style={{ color: theme.colors.primary }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Senha</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Gerenciada pelo Google
                  </div>
                </div>
                <Button
                  $variant="secondary"
                  onClick={() => window.open('https://myaccount.google.com/security', '_blank')}
                >
                  Alterar Senha
                </Button>
              </InfoCard>
            </div>
          </Section>
        );

      case 'stats':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.success}>
                  <BarChart3 size={20} />
                </SectionIcon>
                <SectionName>Estatísticas</SectionName>
              </SectionTitle>
            </SectionHeader>

            <StatGrid>
              <StatCard>
                <StatValue>{leads.length}</StatValue>
                <StatLabel>Leads Criados</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{vendasPagas.length}</StatValue>
                <StatLabel>Vendas Fechadas</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>R$ {receitaTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</StatValue>
                <StatLabel>Receita Total</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{vendas.length}</StatValue>
                <StatLabel>Vendas Totais</StatLabel>
              </StatCard>
            </StatGrid>

            <div style={{ marginTop: theme.spacing[6] }}>
              <InfoCard>
                <Calendar size={20} style={{ color: theme.colors.primary }} />
                <div>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Membro desde</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString('pt-BR') : 'N/A'}
                  </div>
                </div>
              </InfoCard>
            </div>
          </Section>
        );

      case 'preferences':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.info}>
                  <Settings size={20} />
                </SectionIcon>
                <SectionName>Preferências</SectionName>
              </SectionTitle>
            </SectionHeader>

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <InfoCard>
                {darkMode ? <Moon size={20} style={{ color: theme.colors.primary }} /> : <Sun size={20} style={{ color: theme.colors.warning }} />}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Tema {darkMode ? 'Escuro' : 'Claro'}</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Alterar aparência do sistema
                  </div>
                </div>
                <ToggleSwitch $checked={darkMode}>
                  <input
                    type="checkbox"
                    checked={darkMode}
                    onChange={handleToggleTheme}
                  />
                  <span></span>
                </ToggleSwitch>
              </InfoCard>

              <InfoCard>
                <Bell size={20} style={{ color: theme.colors.primary }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Notificações por Email</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Receba atualizações importantes por email
                  </div>
                </div>
                <ToggleSwitch $checked={emailNotifications}>
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={handleToggleEmailNotifications}
                  />
                  <span></span>
                </ToggleSwitch>
              </InfoCard>

              <InfoCard>
                <Zap size={20} style={{ color: theme.colors.warning }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Notificações no Painel de Controle</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Popups de sucesso e avisos (ex: "Configurações do Google salvas")
                  </div>
                </div>
                <ToggleSwitch $checked={panelNotifications}>
                  <input
                    type="checkbox"
                    checked={panelNotifications}
                    onChange={handleTogglePanelNotifications}
                  />
                  <span></span>
                </ToggleSwitch>
              </InfoCard>

              <InfoCard>
                <Globe size={20} style={{ color: theme.colors.info }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Idioma</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Português (Brasil)
                  </div>
                </div>
              </InfoCard>
            </div>
          </Section>
        );

      case 'subscription':
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>
                <SectionIcon $color={theme.colors.warning}>
                  <CreditCard size={20} />
                </SectionIcon>
                <SectionName>Assinatura</SectionName>
              </SectionTitle>
            </SectionHeader>

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <InfoCard>
                <Zap size={20} style={{ color: theme.colors.primary }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                    Plano {subscriptionStatus.plano || 'Free'}
                  </div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Status: {subscriptionStatus.ativo ? 'Ativo' : 'Inativo'}
                  </div>
                </div>
                <Button
                  $variant="primary"
                  onClick={() => window.location.href = '/upgrade'}
                >
                  Fazer Upgrade
                </Button>
              </InfoCard>

              {subscriptionStatus.ativo && (
                <>
                  <InfoCard>
                    <Calendar size={20} style={{ color: theme.colors.info }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Plano iniciado em</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                        {user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                    </div>
                  </InfoCard>

                  <InfoCard>
                    <Calendar size={20} style={{ color: theme.colors.warning }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Próxima fatura</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                        {subscriptionStatus.validade ? new Date(subscriptionStatus.validade).toLocaleDateString('pt-BR') : 'N/A'}
                      </div>
                    </div>
                  </InfoCard>

                  <InfoCard>
                    <Download size={20} style={{ color: theme.colors.success }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Faturas</div>
                      <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                        Visualize e baixe suas faturas
                      </div>
                    </div>
                    <Button $variant="secondary">Ver Faturas</Button>
                  </InfoCard>
                </>
              )}
            </div>
          </Section>
        );

      case 'danger':
        return (
          <DangerZone>
            <DangerTitle>Zona de Perigo</DangerTitle>
            <p style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing[6] }}>
              Ações irreversíveis que afetam permanentemente sua conta.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
              <InfoCard style={{ background: theme.colors.background }}>
                <Download size={20} style={{ color: theme.colors.info }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Exportar Dados</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Baixe todos os seus dados em formato JSON
                  </div>
                </div>
                <Button $variant="secondary">
                  <Download size={16} />
                  Exportar
                </Button>
              </InfoCard>

              <InfoCard style={{ background: theme.colors.background }}>
                <LogOut size={20} style={{ color: theme.colors.warning }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Sair da Conta Google</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Desconecte sua conta Google do Loopra
                  </div>
                </div>
                <Button $variant="secondary" onClick={handleLogout}>
                  <LogOut size={16} />
                  Sair
                </Button>
              </InfoCard>

              <InfoCard style={{ background: theme.colors.background }}>
                <AlertTriangle size={20} style={{ color: theme.colors.error }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.textPrimary }}>Excluir Conta</div>
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
                    Exclua permanentemente sua conta e todos os dados
                  </div>
                </div>
                <Button $variant="danger">
                  Excluir Conta
                </Button>
              </InfoCard>
            </div>
          </DangerZone>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ProfileContainer>
        <HiddenInput
          ref={bannerInputRef}
          type="file"
          accept="image/*"
          onChange={handleBannerChange}
        />
        <HeroSection $bannerUrl={bannerPhoto || undefined} onClick={handleBannerClick}>
          <BannerUploadIcon>
            <Camera size={20} />
          </BannerUploadIcon>
          {/* Banner sem foto de perfil e sem nome - apenas o banner */}
        </HeroSection>

        <ContentLayout>
          <Sidebar>
            <SidebarItem $active={activeTab === 'personal'} onClick={() => setActiveTab('personal')}>
              <User size={18} />
              Informações Pessoais
            </SidebarItem>
            <SidebarItem $active={activeTab === 'professional'} onClick={() => setActiveTab('professional')}>
              <Briefcase size={18} />
              Profissional
            </SidebarItem>
            <SidebarItem $active={activeTab === 'security'} onClick={() => setActiveTab('security')}>
              <Shield size={18} />
              Segurança
            </SidebarItem>
            <SidebarItem $active={activeTab === 'stats'} onClick={() => setActiveTab('stats')}>
              <BarChart3 size={18} />
              Estatísticas
            </SidebarItem>
            <SidebarItem $active={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')}>
              <Settings size={18} />
              Preferências
            </SidebarItem>
            <SidebarItem $active={activeTab === 'subscription'} onClick={() => setActiveTab('subscription')}>
              <CreditCard size={18} />
              Assinatura
            </SidebarItem>
            <SidebarItem $active={activeTab === 'danger'} onClick={() => setActiveTab('danger')}>
              <AlertTriangle size={18} />
              Zona de Perigo
            </SidebarItem>
          </Sidebar>

          <MainContent>
            {renderContent()}
          </MainContent>
        </ContentLayout>
      </ProfileContainer>

      {/* Modal de visualização da foto */}
      <PhotoModal $visible={showPhotoModal} onClick={() => setShowPhotoModal(false)}>
        <PhotoModalContent onClick={(e) => e.stopPropagation()}>
          <PhotoModalClose onClick={() => setShowPhotoModal(false)}>
            <X size={20} />
          </PhotoModalClose>
          <PhotoModalImage
            src={profilePhoto || user?.photoURL || ''}
            alt="Foto de perfil em alta qualidade"
          />
        </PhotoModalContent>
      </PhotoModal>
    </>
  );
};

export default Profile;
